'use client';

import { useState, useEffect } from 'react';

export interface PolicyVersion {
  id: string;
  policy_type: 'privacy_policy' | 'terms_acceptance' | 'cookies' | 'marketing';
  version: string;
  content: string;
  effective_date: string | null;
  updated_at: string;
  status: 'draft' | 'active' | 'archived';
  created_at: string;
  published_at: string | null;
  created_by: string | null;
}

interface UsePolicyResult {
  policy: PolicyVersion | null;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to fetch active policy version by type
 * @param policyType - The type of policy to fetch ('privacy_policy', 'terms_acceptance', or 'cookies')
 * @returns Object with policy data, loading state, and error state
 */
export function usePolicy(policyType: 'privacy_policy' | 'terms_acceptance' | 'cookies'): UsePolicyResult {
  const [policy, setPolicy] = useState<PolicyVersion | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchPolicy() {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/policy-versions/public?type=${policyType}`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch policy: ${response.statusText}`);
        }

        const result = await response.json();

        if (!isMounted) return;

        if (result.success) {
          setPolicy(result.data);
        } else {
          setError(result.error || 'Failed to fetch policy');
          setPolicy(null);
        }
      } catch (err) {
        if (!isMounted) return;
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch policy';
        setError(errorMessage);
        setPolicy(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchPolicy();

    return () => {
      isMounted = false;
    };
  }, [policyType]);

  return { policy, loading, error };
}

